import { Subject, debounceTime } from "rxjs";
import { CacheService } from "../services/cache.service";

/**
 * Wraps a string so we can use the proxy object to persist the value
 */
export class StringWrapper<T extends string> {
  public value: T;
}

interface StorageHandler<T extends object> {
  get: () => T | null;
  set: (object: object) => void;
  delete: () => void;
}

function getStorageHandler<T extends object>(cacheService: CacheService, cacheKey: string, ttl: number | undefined): StorageHandler<T> {
  return {
    get: () => cacheService.getJsonSession(cacheKey) as T,
    set: (object: object) => {
      cacheService.setJsonSession(cacheKey, object, ttl);
    },
    delete: () => {
      cacheService.deleteSession(cacheKey);
    },
  };
}
export class PersistentObjectFactory<T extends object = object> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onPropertyChange = new Subject<Record<string, any>>();
  private _ttlDuration: number | undefined = 1000 * 60 * 60;
  private _storageHandler: StorageHandler<T>;

  constructor(private _cacheService: CacheService, private _cacheKey: string, private _customStorageHandler?: StorageHandler<T>) {
    this._storageHandler = this._getStorageHandler();

    this._onPropertyChange.pipe(debounceTime(10)).subscribe((object) => {
      this.save(object);
    });
  }

  private _getStorageHandler(): StorageHandler<T> {
    return this._customStorageHandler || getStorageHandler(this._cacheService, this._cacheKey, this._ttl);
  }

  public set ttlDuration(duration: number | undefined) {
    this._ttlDuration = duration;
    this._storageHandler = this._getStorageHandler();
  }

  private get _ttl(): number | undefined {
    if (this._ttlDuration === undefined) return undefined;
    return Date.now() + this._ttlDuration;
  }

  public create(object: T): T {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const cache = this._storageHandler.get();

    const proxy = new Proxy(cache || object, {
      set(target, prop, val) {
        self._onPropertyChange.next({ ...target, [prop]: val });

        return Reflect.set(target, prop, val);
      },
    });

    return proxy;
  }

  public save(object: object): void {
    this._storageHandler.set(object);
  }

  public clear(object: object): void {
    for (const key of Object.keys(object)) {
      delete object[key];
    }

    this._storageHandler.delete();
  }
}
